import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Textbox } from "capitalrx-components-2.0/lib/forms";
import { Icon, LoadingGroup, LoadingSkeleton, useAlert } from "capitalrx-components-2.0/lib/general";
import { Grid, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Subtitle } from "capitalrx-components-2.0/lib/typography";
import { debounce } from "capitalrx-components-2.0/lib/utilities";
import Decimal from "decimal.js";

import { useListMemberDependentsQuery, useListMemberMedicalServiceMaximumsQuery } from "@/App/api/member";
import { ContentPanel, DependentDropdown, PageWrapper } from "@/App/components";
import { MemberEligibilityContext } from "@/App/context";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";
import { MedicalCareUsageAccumulationValues, MedicalCareUsageNetworks, MedicalCareUsageUnits } from "@/App/types/medicalServiceMaximums";

import { MedicalCareUsageAccumulatorCard } from "./MedicalCareUsageAccumulatorCard";

import "./MedicalCareUsageLimitsPage.scss";

const TRANSLATION_PATH = "MEDICAL_CARE_USAGE_LIMITS.medicalCareUsageLimitsPage";

const logSearchAnalytics = debounce((value: string) => {
  if (value) {
    logEvent(ANALYTIC_EVENTS.CARE_USAGE_AND_LIMITS.searchCare);
  }
}, 600);

interface SelectedPerson {
  readonly personCode: string;
  readonly personName: string;
}

export const MedicalCareUsageLimitsPage = () => {
  const { memberEligibility } = useContext(MemberEligibilityContext);

  const { t: translate } = useTranslation();

  const [selectedPerson, setSelectedPerson] = useState<SelectedPerson>({
    personCode: memberEligibility.member.person_code,
    personName: `${memberEligibility.member.first_name} ${memberEligibility.member.last_name} (${translate(`${TRANSLATION_PATH}.self`)})`,
  });
  const [searchValue, setSearchValue] = useState<string>("");

  const { data: dependents } = useListMemberDependentsQuery();
  const minorDependents = useMemo(() => {
    return (dependents?.results || []).filter((dependent) => dependent.member?.is_minor_dependent);
  }, [dependents]);

  const {
    data: medicalCareUsagesData,
    isLoading: isLoadingMedicalCareUsagesData,
    isRefetching: isRefetchingMedicalCareUsagesData,
    isError: isErrorMedicalCareUsagesData,
    refetch: refetchMedicalCareUsagesData,
  } = useListMemberMedicalServiceMaximumsQuery({ person_code: selectedPerson.personCode });

  const medicalCareUsages = useMemo(() => {
    if (isLoadingMedicalCareUsagesData || isRefetchingMedicalCareUsagesData || isErrorMedicalCareUsagesData) return [];

    const units = Object.values(MedicalCareUsageUnits) as string[];

    return Object.entries(medicalCareUsagesData).flatMap(([serviceName, maximumNetwork]) =>
      Object.values(maximumNetwork).map((maximum) => ({
        title: serviceName,
        inNetwork: maximum.networks && maximum.networks.length > 0 && maximum.networks.includes(MedicalCareUsageNetworks.InNetwork),
        outOfNetwork: maximum.networks && maximum.networks.length > 0 && maximum.networks.includes(MedicalCareUsageNetworks.OutOfNetwork),
        accumulator: {
          max: new Decimal(maximum.maximum),
          remaining: new Decimal(maximum.remaining),
          spent: new Decimal(maximum.used),
          unit: units.includes(maximum.type)
            ? (maximum.type as MedicalCareUsageUnits[keyof MedicalCareUsageUnits])
            : MedicalCareUsageUnits.Unit,
        } as MedicalCareUsageAccumulationValues,
      })),
    );
  }, [medicalCareUsagesData, isLoadingMedicalCareUsagesData, isRefetchingMedicalCareUsagesData]);

  useEffect(() => {
    refetchMedicalCareUsagesData();
  }, [selectedPerson]);

  const handleDependentChange = (value: string) => {
    const dependent = minorDependents.find((d) => d.member.person_code === value);
    if (value === memberEligibility.member.person_code) {
      setSelectedPerson({
        personCode: value,
        personName: `${memberEligibility.member.first_name} ${memberEligibility.member.last_name} (${translate(`${TRANSLATION_PATH}.self`)})`,
      });
    } else if (dependent) {
      setSelectedPerson({
        personCode: value,
        personName: `${dependent.member.first_name} ${dependent.member.last_name} (${translate(`${TRANSLATION_PATH}.dependent`)})`,
      });
    }
    logEvent(ANALYTIC_EVENTS.CARE_USAGE_AND_LIMITS.dependentFilterChange);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    logSearchAnalytics(value);
  };

  const filteredMedicalCareUsages = useMemo(() => {
    if (!searchValue) return medicalCareUsages;
    return medicalCareUsages.filter((care) => care.title.toLowerCase().includes(searchValue.toLowerCase()));
  }, [medicalCareUsages, searchValue]);

  const { cardsColumns, searchColumns, dropdownColumns } = useResponsive({
    searchColumns: {
      desktop: 4,
      tablet: 12,
      mobile: 12,
    },
    dropdownColumns: {
      desktop: 4,
      tablet: 6,
      mobile: 12,
    },
    cardsColumns: {
      desktop: 4,
      tablet: 6,
      mobile: 12,
    },
  });

  const showAlert = useAlert();

  if (isErrorMedicalCareUsagesData) {
    showAlert({
      message: translate(`${TRANSLATION_PATH}.noData`),
      type: "error",
      style: "toast",
    });
  }

  return (
    <PageWrapper>
      <Stack>
        <ContentPanel showBorder title={<Subtitle>{translate(`${TRANSLATION_PATH}.careUsageAndLimits`)}</Subtitle>}>
          <Stack spacing="large">
            <Stack>
              <Body>{translate(`${TRANSLATION_PATH}.careUsageAndLimitsDescription`)}</Body>
              <Body>{translate(`${TRANSLATION_PATH}.careUsageAndLimitsExplanation`)}</Body>
              <Body>{translate(`${TRANSLATION_PATH}.noPresentLimitsMessage`)}</Body>
            </Stack>
            <Grid>
              <Grid.Span columns={dropdownColumns}>
                <DependentDropdown
                  testId="care-dependents-view-dropdown"
                  onChange={handleDependentChange}
                  value={selectedPerson.personCode}
                  label={translate(`${TRANSLATION_PATH}.viewingCareFor`)}
                  aria-label={translate(`${TRANSLATION_PATH}.dependentsDropdownLabel`)}
                  dependents={minorDependents}
                  member={memberEligibility.member}
                />
              </Grid.Span>
            </Grid>
            <Grid>
              <Grid.Span columns={searchColumns}>
                <Textbox
                  testId="search-care-textbox"
                  label={translate(`${TRANSLATION_PATH}.searchForCare`)}
                  placeholder={translate(`${TRANSLATION_PATH}.search`)}
                  prefix={<Icon name="search" />}
                  value={searchValue}
                  onChange={handleSearchChange}
                  aria-label={translate(`${TRANSLATION_PATH}.searchForCare`)}
                />
              </Grid.Span>
            </Grid>
            <LoadingGroup loading={isLoadingMedicalCareUsagesData || isRefetchingMedicalCareUsagesData}>
              <LoadingGroup.Placeholder
                fallback={
                  <Grid>
                    <Grid.Span columns={cardsColumns}>
                      <LoadingSkeleton style={{ height: 202, width: "100%" }} />
                    </Grid.Span>
                    <Grid.Span columns={cardsColumns}>
                      <LoadingSkeleton style={{ height: 202, width: "100%" }} />
                    </Grid.Span>
                  </Grid>
                }
              >
                <Grid>
                  {filteredMedicalCareUsages.map(({ title, inNetwork, outOfNetwork, accumulator }, index) => (
                    <Grid.Span columns={cardsColumns} key={index}>
                      <MedicalCareUsageAccumulatorCard
                        title={title}
                        inNetwork={inNetwork}
                        outOfNetwork={outOfNetwork}
                        accumulator={accumulator}
                      />
                    </Grid.Span>
                  ))}
                </Grid>
              </LoadingGroup.Placeholder>
            </LoadingGroup>
          </Stack>
        </ContentPanel>
      </Stack>
    </PageWrapper>
  );
};
